/**
 * Platform and content: config, exchange rates, public agency page, partner lead form.
 * Endpoints: /api/config/platform, /api/currency/rates, /api/agency/:agencyName (cars of an agency),
 * /api/client/agencies/:id, /api/agencyRequests, /api/newsletter/add, /api/send-email (contact form).
 */
import { z } from 'zod';
import { ns, translate } from '@/i18n';
import type { Agency, BlogPost, ContactMessage, Currency, PartnerLead, PlatformConfig, VehicleSummary } from '../../domain';
import { assetUrl } from './media';
import type { PublicAgency } from './vehicles';
import { toAgencySummary } from './vehicles';

const n = z.number().nullish();
const s = z.string().nullish();

export const PlatformConfigSchema = z
  .object({
    longStayDiscounts: z
      .object({ enabled: z.boolean().nullish(), weeklyPct: n, monthlyPct: n })
      .passthrough()
      .nullish(),
    onlinePaymentMode: s,
    minRentalDays: n,
    activitiesEnabled: z.boolean().nullish(),
    popularDestinations: z.array(z.object({ city: z.string(), imageUrl: s }).passthrough()).nullish(),
  })
  .passthrough();
export type PlatformConfigRecord = z.infer<typeof PlatformConfigSchema>;

export const RatesSchema = z
  .object({ base: s, rates: z.object({ EUR: n, USD: n }).passthrough().nullish(), source: s })
  .passthrough();

export interface LongStay {
  enabled: boolean;
  weeklyPct: number;
  monthlyPct: number;
}

export const longStayOf = (c: PlatformConfigRecord | null | undefined): LongStay => ({
  enabled: !!c?.longStayDiscounts?.enabled,
  weeklyPct: Number(c?.longStayDiscounts?.weeklyPct) || 0,
  monthlyPct: Number(c?.longStayDiscounts?.monthlyPct) || 0,
});

/**
 * `activitiesEnabled` is both the build flag (REACT_APP_ACTIVITIES_ENABLED, as on the old site) and the
 * server's. Rates: the live rates of `GET /api/currency/rates`; a missing, null or invalid one (or the call
 * failed) is null, so that currency falls back to MAD (API-FX-1, the old site's `resolveEffectiveCurrency`).
 */
export function toPlatformConfig(
  c: PlatformConfigRecord,
  rates: z.infer<typeof RatesSchema> | null,
  buildActivitiesEnabled: boolean,
): PlatformConfig {
  const ls = longStayOf(c);
  const live = (cur: 'EUR' | 'USD') => {
    const raw = rates?.rates?.[cur];
    return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null;
  };
  const out: Record<Currency, number | null> = { MAD: 1, EUR: live('EUR'), USD: live('USD') };
  return {
    paymentMode: c.onlinePaymentMode === 'commission_only' ? 'commission_only' : 'full',
    activitiesEnabled: buildActivitiesEnabled && c.activitiesEnabled !== false,
    longStay: ls.enabled ? { weeklyPct: ls.weeklyPct, monthlyPct: ls.monthlyPct } : { weeklyPct: 0, monthlyPct: 0 },
    rates: out,
    minRentalDays: Math.max(1, Number(c.minRentalDays) || 1),
  };
}

/** `/pages/agency/:agencyName`: the agency card from its public profile and its vehicles. */
export function toAgency(slug: string, agency: PublicAgency, vehicles: VehicleSummary[]): Agency {
  const summary = toAgencySummary(agency, agency.city ?? '');
  const zone = agency.zone ? `${agency.zone}, ` : '';
  return {
    ...summary,
    slug,
    description: translate(
      ns.labels,
      agency.deliveryEnabled === true || agency.deliveryEnabled === 'true'
        ? 'agency.description.delivery'
        : 'agency.description.agency',
      { city: summary.city },
    ),
    address: `${zone}${summary.city}`,
    memberSince: (agency.createdAt ?? new Date().toISOString()).slice(0, 10),
    vehicles,
  };
}

/** Lead type → AgencyRequest.agencyType (`cars | bikes | both`). Experiences have no type there. */
export const AGENCY_TYPE: Record<PartnerLead['type'], string | null> = {
  cars: 'cars',
  motos: 'bikes',
  activities: null,
};

/** Multipart body of POST /api/agencyRequests (API-AGY-7: one `phoneNumbers[]` entry per phone). */
export function partnerLeadForm(lead: PartnerLead, agencyType: string): FormData {
  const f = new FormData();
  f.append('agencyName', lead.company);
  f.append('managerName', lead.contactName);
  for (const p of lead.phones) f.append('phoneNumbers[]', p);
  f.append('capacity', lead.fleetSize);
  f.append('email', lead.email);
  f.append('city', lead.city);
  if (lead.zone) f.append('zone', lead.zone);
  f.append('address', lead.address);
  f.append('agencyType', agencyType);
  if (lead.photo) f.append('managerAvatar', lead.photo);
  return f;
}

/* ── Blog (routes not mounted by the backend today: F-05) ───────────────── */

const SectionSchema = z.object({ title: s, content: s }).passthrough();
const BlogRecordSchema = z
  .object({
    _id: z.string(),
    title: z.string(),
    mainImage: s,
    sections: z.array(SectionSchema).nullish(),
    createdAt: s,
    customUpdatedAt: s,
    category: s,
  })
  .passthrough();
export const BlogListSchema = z.array(BlogRecordSchema);
/** `GET /api/blogs/:id` answered `{ post }` on the old site; a bare post is accepted too. */
export const BlogPostSchema = z.union([z.object({ post: BlogRecordSchema }).passthrough(), BlogRecordSchema]);

/** CMS HTML → plain paragraphs (the domain never carries markup). */
export const stripHtml = (html: string) =>
  html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h\d|li)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, '’');

export function toBlogPost(p: z.infer<typeof BlogRecordSchema>, baseUrl: string): BlogPost {
  const paragraphs = (p.sections ?? [])
    .flatMap((sec) => [sec.title ?? '', ...stripHtml(sec.content ?? '').split(/\n+/)])
    .map((x) => x.trim())
    .filter(Boolean);
  const words = paragraphs.join(' ').split(/\s+/).length;
  return {
    id: p._id,
    title: p.title,
    excerpt: paragraphs.find((x) => x.length > 40) ?? paragraphs[0] ?? '',
    image: assetUrl(baseUrl, p.mainImage, ''),
    imageAlt: p.title,
    date: (p.customUpdatedAt ?? p.createdAt ?? new Date().toISOString()).slice(0, 10),
    category: p.category ?? translate(ns.labels, 'blogCategory'),
    readingMinutes: Math.max(1, Math.round(words / 200)),
    paragraphs,
  };
}

/* ── Contact form (P14, API-CNT-4) ── */

/** What `POST /api/send-email` receives: exactly the legacy form's body (`contact.tsx:254-267`). */
export interface ContactBody {
  name: string;
  email: string;
  subject: string;
  message: string;
}

/**
 * The legacy rules: «[URGENT] » before the subject when urgent (an empty subject reads «Message»), and
 * «Téléphone: <phone>» plus a blank line before the message when a phone is given. The backend also reads a
 * `tel` field, which the legacy form never sent: it is not sent either (the phone stays in the message).
 */
export function contactBody(m: ContactMessage): ContactBody {
  const phone = m.phone?.trim() ?? '';
  return {
    name: m.name,
    email: m.email,
    subject: `${m.urgent ? '[URGENT] ' : ''}${m.subject || 'Message'}`,
    // i18n-ignore-next-line: the legacy request body, read by the support team (Decision 90), not shown
    message: (phone ? `Téléphone: ${phone}\n\n` : '') + m.message,
  };
}
